// One place that loads a clinic's patient data.
// Demo mode: the in-memory sample lists (mockData). Real mode: the clinic's records in
// Firestore (Mumbai), read under the signed-in user's permissions (firestore.rules).
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import type { Baby, Call, DailyStats, Dose, DoseStatus, Facility, Flag, Mother, ProtocolStep } from '../types';
import { dayDiff, parseDate, startOfToday } from '../lib/dates';
import { db, firebaseEnabled } from '../lib/firebase';
import * as mock from './mockData';

export interface ClinicData {
  mothers: Mother[];
  babies: Baby[];
  doses: Dose[];
  calls: Call[];
  flags: Flag[];
  /** Attendance marks for pregnancy check-ups, keyed `${motherId}-${stepId}`. */
  milestoneMarks: Record<string, DoseStatus>;
  protocol: ProtocolStep[];
  /** Real data derives "missed" for unmarked past items; the demo assumes they were attended. */
  real: boolean;
}

export const isReal = () => firebaseEnabled && !!db;

// Real data: a dose is only "attended" when someone marked it. Unmarked and more than
// 3 days past due = missed (the baby may be unprotected). Stored attended/missed are kept.
export function realStatus(stored: DoseStatus | undefined, dueDate: string): DoseStatus {
  if (stored === 'attended' || stored === 'missed') return stored;
  const diff = dayDiff(startOfToday(), parseDate(dueDate));
  return diff < -3 ? 'missed' : diff <= 0 ? 'due' : 'upcoming';
}

async function list<T>(fid: string, name: string): Promise<T[]> {
  const snap = await getDocs(collection(db!, 'facilities', fid, name));
  return snap.docs.map((d) => ({ ...(d.data() as T), id: d.id }));
}

/** Archived TEST mothers (and their babies and doses) are hidden everywhere. */
function withoutArchived(c: ClinicData): ClinicData {
  const gone = new Set(c.mothers.filter((m) => m.archived === true).map((m) => m.id));
  if (!gone.size) return c;
  const babies = c.babies.filter((b) => !gone.has(b.motherId));
  const keep = new Set(babies.map((b) => b.id));
  return { ...c, mothers: c.mothers.filter((m) => !gone.has(m.id)), babies, doses: c.doses.filter((d) => keep.has(d.babyId)), calls: c.calls.filter((x) => !gone.has(x.motherId)), flags: c.flags.filter((f) => !gone.has(f.motherId)) };
}

export async function loadClinic(facilityId: string): Promise<ClinicData> {
  return withoutArchived(await loadClinicAll(facilityId));
}

async function loadClinicAll(facilityId: string): Promise<ClinicData> {
  if (!isReal()) {
    return {
      mothers: mock.mothers.filter((m) => m.facilityId === facilityId),
      babies: mock.babies.filter((b) => b.facilityId === facilityId),
      doses: mock.doses.filter((d) => d.facilityId === facilityId),
      calls: mock.calls.filter((c) => c.facilityId === facilityId),
      flags: mock.flags.filter((f) => f.facilityId === facilityId),
      milestoneMarks: mock.milestoneMarks,
      protocol: mock.clinicProtocols[facilityId] ?? mock.defaultProtocol,
      real: false,
    };
  }
  const [mothers, babies, doses, calls, flags, marks, protocol] = await Promise.all([
    list<Mother>(facilityId, 'mothers'),
    list<Baby>(facilityId, 'babies'),
    list<Dose>(facilityId, 'doses'),
    list<Call>(facilityId, 'calls'),
    list<Flag & { acknowledgedByName?: string }>(facilityId, 'flags'),
    list<{ id: string; status: DoseStatus }>(facilityId, 'milestoneMarks'),
    list<ProtocolStep>(facilityId, 'protocols'),
  ]);
  return {
    mothers,
    babies,
    doses: doses.map((d) => ({ ...d, status: realStatus(d.status, d.dueDate) })),
    calls,
    // The database stores who acknowledged as a user id (checked by the rules); show the name.
    flags: flags.map((f) => ({ ...f, acknowledgedBy: f.acknowledgedByName ?? f.acknowledgedBy })),
    milestoneMarks: Object.fromEntries(marks.map((m) => [m.id, m.status])),
    protocol: protocol.length ? protocol : mock.defaultProtocol,
    real: true,
  };
}

// The clinic's own settings and branding (facilities/{id}).
export async function loadFacility(facilityId: string): Promise<Facility> {
  if (!facilityId) throw new Error('No clinic selected');
  const snap = await getDoc(doc(db!, 'facilities', facilityId));
  if (!snap.exists()) throw new Error(`Facility ${facilityId} not found`);
  return withFacilityDefaults({ ...(snap.data() as Facility), id: snap.id });
}

/**
 * Fills safe blanks for any setting a clinic record is missing (e.g. the server-made Test Clinic has only the basics),
 * so a screen never crashes on it. Blanks never tick a go-live item or turn anything on.
 */
export function withFacilityDefaults(f: Partial<Facility> & { id: string }): Facility {
  const blank = { name: '', phone: '' };
  return {
    legalName: '', displayNameEn: f.id, displayNameHi: '', shortName: '', logoUrl: '', brandColor: '', address: '', city: '', state: '', pin: '',
    mainPhone: '', outboundNumber: '', whatsappNumber: '', languages: ['hi'], voice: 'female', retryRule: '',
    outOfHoursPlan: '', numberMode: 'platform', status: 'setup',
    ...f,
    callingHours: { start: '09:00', end: '20:00', ...f.callingHours },
    services: { baby: false, pregnancy: false, ...f.services },
    doctors: f.doctors ?? [],
    escalationContact: { ...blank, ...f.escalationContact },
    outOfHoursContact: { ...blank, ...f.outOfHoursContact },
    goLive: { script: false, consent: false, number: false, testCalls: false, escalation: false, ...f.goLive },
  } as Facility;
}

// Super admin only: every clinic's settings (never patient data).
export async function loadFacilities(): Promise<Facility[]> {
  const snap = await getDocs(collection(db!, 'facilities'));
  return snap.docs.map((d) => withFacilityDefaults({ ...(d.data() as Facility), id: d.id }));
}

// Daily counts written by the backend, oldest first. Document id = date (YYYY-MM-DD).
export async function loadDailyStats(facilityId: string, days: number): Promise<DailyStats[]> {
  const rows = await list<DailyStats>(facilityId, 'dailyStats');
  return rows.sort((a, b) => a.date.localeCompare(b.date)).slice(-days);
}

// Transcripts live in a separate document that only doctors and facility admins can read.
export async function loadTranscript(facilityId: string, callId: string): Promise<string | undefined> {
  if (!isReal()) return mock.calls.find((c) => c.id === callId)?.transcript;
  try {
    const snap = await getDoc(doc(db!, 'facilities', facilityId, 'calls', callId, 'private', 'transcript'));
    return snap.exists() ? (snap.data().text as string) : undefined;
  } catch {
    return undefined;
  }
}
